import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import type { InputRef } from "ui/forms/Input";
import Input from "ui/forms/Input";
import IconClose from "ui/icons/IconClose";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import IconSearch from "ui/icons/IconSearch";
import { Popover } from "ui/popovers/Popover/Popover";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./MultiSelect.module.scss";

interface MultiSelectOptionBase {
  disabled?: boolean;
  tooltip?: ReactNode;
}

export interface MultiSelectOption extends MultiSelectOptionBase {
  label: string;
  value: string;
  /** Optional inline element rendered immediately after the option label (e.g., notice/warning badge) */
  labelInlineNotice?: ReactNode;
  options?: undefined;
}

export interface MultiSelectOptionGroup extends MultiSelectOptionBase {
  label?: ReactNode;
  options: MultiSelectOption[];
  value?: undefined;
}

type MultiSelectOptions = MultiSelectOption[] | MultiSelectOptionGroup[];

interface MultiSelectProps {
  /** An array of options or option groups */
  options: MultiSelectOptions;
  /** The currently selected values */
  value: string[];
  /** The callback when the selected values change */
  onChange: (value: string[]) => void;
  /** The placeholder text for the select input */
  placeholder?: string;
  /** Whether to force the placeholder to be shown even if there are selected values */
  forcePlaceholder?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether to show the filter input */
  filterable?: boolean;
  /** Whether to show select all/deselect all options */
  showBulkActions?: boolean;
  /** Whether to show a badge with the number of selected values */
  showCountBadge?: boolean;
  /** The maximum height of the option list in pixels (excluding the filter and bulk actions) */
  maxHeight?: number;
  className?: string;
  /** Optional notification element shown inside the trigger (left side), for warnings/tips/status */
  icon?: ReactNode;
}

export default function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select options",
  forcePlaceholder = false,
  disabled = false,
  filterable = false,
  showBulkActions = false,
  showCountBadge = false,
  maxHeight = 350,
  className,
  icon,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const filterInputRef = useRef<InputRef>(null);

  /** A flattened array of all options */
  const allOptions = useMemo(() => {
    return options.flatMap((option) => (option.options ? option.options : [option]));
  }, [options]);

  /** Disable bulk actions & filtering if only 1 option */
  if (allOptions.length < 2) {
    filterable = false;
    showBulkActions = false;
  }

  /** A filtered array of options that maintains grouping */
  const filteredOptionsByGroup = useMemo(() => {
    return options
      .map((option) => {
        if (option.options) {
          const filteredOptionGroup: MultiSelectOptionGroup = {
            ...option,
            options: option.options.filter((option) => option.label.toLowerCase().includes(filterQuery.toLowerCase())),
          };

          return filteredOptionGroup;
        }

        return option;
      })
      .filter((option) =>
        option.options ? option.options.length > 0 : option.label.toLowerCase().includes(filterQuery.toLowerCase()),
      );
  }, [options, filterQuery]);

  /** A filtered array of options */
  const filteredOptions = useMemo(() => {
    if (!filterQuery) {
      return allOptions;
    }

    return allOptions.filter((option) => option.label.toLowerCase().includes(filterQuery.toLowerCase()));
  }, [allOptions, filterQuery]);

  const selectedLabels = useMemo(() => {
    return allOptions.filter((option) => value.includes(option.value)).map((option) => option.label);
  }, [allOptions, value]);

  /** Flag if all options are selected */
  const allSelected = useMemo(() => {
    const selectableOptions = allOptions.filter((option) => !option.disabled);
    return selectableOptions.length > 0 && selectableOptions.every((option) => value.includes(option.value));
  }, [allOptions, value]);

  /** Flag if some options are selected */
  const someSelected = useMemo(() => {
    const selectableOptions = allOptions.filter((option) => !option.disabled);
    return selectableOptions.some((option) => value.includes(option.value)) && !allSelected;
  }, [allOptions, allSelected, value]);

  const handleBulkAction = useCallback(
    (checked: boolean) => {
      const selectableOptions = allOptions.filter((option) => !option.disabled);
      const newValues = checked ? selectableOptions.map((option) => option.value) : [];

      onChange(newValues);
    },
    [allOptions, onChange],
  );

  const handleOptionChange = useCallback(
    (optionValue: string, checked: boolean) => {
      const newValues = checked ? [...value, optionValue] : value.filter((v) => v !== optionValue);
      onChange(newValues);
    },
    [value, onChange],
  );

  // Reset focus when dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(-1);
      setFilterQuery("");
    }
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isOpen]);

  const renderOption = useCallback(
    (option: MultiSelectOption, index: number) => {
      const isSelected = value.includes(option.value);
      const isFocused = focusedIndex === index;
      const labelNode = (
        <span>
          {option.label}
          {option.labelInlineNotice}
        </span>
      );

      return (
        <Checkbox
          key={`${option.value}-${index}`}
          role="option"
          data-testid={`option-${option.value}-${index}`}
          className={cx(styles.option, {
            [styles.selected]: isSelected,
            [styles.focused]: isFocused,
            [styles.disabled]: option.disabled,
          })}
          tooltip={option.tooltip}
          label={labelNode}
          checked={isSelected}
          disabled={option.disabled}
          onChangeBoolean={(checked) => {
            if (!option.disabled) {
              handleOptionChange(option.value, checked);
            }
          }}
          onMouseEnter={() => {
            if (!option.disabled) {
              setFocusedIndex(index);
            }
          }}
        />
      );
    },
    [value, focusedIndex, handleOptionChange],
  );

  const renderOptionGroup = useCallback(
    (group: MultiSelectOptionGroup, groupIndex: number) => {
      const label = group.label
        ? wrapWithTooltip(<div className={styles.groupHeader}>{group.label}</div>, group.tooltip)
        : null;
      return (
        <div key={typeof group.label === "string" ? group.label : groupIndex} className={styles.optionGroup}>
          {label}
          {group.options.map((option, optionIndex) => {
            let absoluteIndex = optionIndex;
            for (let i = 0; i < groupIndex; i++) {
              const group = filteredOptionsByGroup[i];
              absoluteIndex += group.options?.length ?? 1;
            }
            return renderOption(option, absoluteIndex);
          })}
          <div className={styles.groupSeparator} />
        </div>
      );
    },
    [renderOption, filteredOptionsByGroup],
  );

  const showPlaceholder = selectedLabels.length === 0 || forcePlaceholder;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          btnStyle="noStyle"
          role="combobox"
          data-testid="multiselect-trigger"
          disabled={disabled}
          className={cx(
            styles.inputWrapper,
            {
              [styles.open]: isOpen,
              [styles.disabled]: disabled,
              [styles.placeholder]: showPlaceholder,
            },
            className,
          )}
        >
          {icon}
          {showCountBadge && selectedLabels.length > 0 && (
            <span data-testid="count-badge" className={styles.countBadge}>
              {selectedLabels.length}
            </span>
          )}
          <span className={styles.inputText}>{!showPlaceholder ? selectedLabels.join(", ") : placeholder}</span>
          <div className={styles.icons}>
            {!showPlaceholder && (
              <div
                data-testid="clear-button"
                className={styles.clearButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                }}
              >
                <IconClose />
              </div>
            )}
            <div
              className={cx(styles.chevron, {
                [styles.open]: isOpen,
              })}
            >
              <IconMiniChevronDown />
            </div>
          </div>
        </Button>
      </Popover.Trigger>
      <Popover.Content className={styles.dropdown} side="bottom" align="start">
        {filterable && (
          <div className={styles.filter}>
            <IconSearch />
            <Input
              ref={filterInputRef}
              data-testid="filter-input"
              wrapperClassName={styles.filterInputWrapper}
              className={styles.filterInput}
              placeholder="Filter..."
              inputSize="medium"
              transparent
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
            />
          </div>
        )}

        {showBulkActions && (
          <div className={styles.bulkActions}>
            <Checkbox
              data-testid="bulk-action"
              className={styles.bulkAction}
              label={allSelected ? "Deselect all" : "Select all"}
              checked={someSelected ? "indeterminate" : allSelected}
              onChangeBoolean={(checked) => {
                handleBulkAction(checked);
              }}
            />
          </div>
        )}

        <div data-testid="options-list" className={styles.optionsList} style={{ maxHeight: `${maxHeight}px` }}>
          {filteredOptionsByGroup.map((option, index) =>
            option.options ? renderOptionGroup(option, index) : renderOption(option, index),
          )}
        </div>

        {filteredOptions.length === 0 && (
          <div data-testid="no-options-found" className={styles.noResults}>
            No results found
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}
